import axios from "axios";
import type { AxiosError, InternalAxiosRequestConfig } from "axios";
import { tokenStore } from "@/lib/token-store";

/**
 * Consistent error shape for all API errors surfaced to the UI.
 */
export interface ApiError {
  message: string;
  statusCode: number;
  field_errors?: Record<string, string[]>;
}

export const client = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || "/api",
  headers: { "Content-Type": "application/json" },
});

// ---------------------------------------------------------------------------
// Request interceptor — inject JWT bearer token
// ---------------------------------------------------------------------------
client.interceptors.request.use((config) => {
  const token = tokenStore.getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ---------------------------------------------------------------------------
// Response interceptor — unwrap envelope, refresh on 401, normalise errors
// ---------------------------------------------------------------------------

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null) {
  failedQueue.forEach((pending) => {
    if (token) {
      pending.resolve(token);
    } else {
      pending.reject(error);
    }
  });
  failedQueue = [];
}

client.interceptors.response.use(
  // Success — unwrap the backend's { success, data, meta?, ... } envelope
  // For paginated responses the envelope carries a top-level `meta` that
  // must be preserved so consumers receive PaginatedResponse<T>.
  (response) => {
    const envelope = response.data;
    const payload = envelope?.data ?? envelope;
    if (envelope?.meta) {
      return { data: payload, meta: envelope.meta };
    }
    return payload;
  },

  // Error — handle 401 refresh, then normalise
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Only attempt refresh for 401s that aren't already retries
    // and aren't auth endpoints themselves (prevent infinite loops)
    const isAuthEndpoint = originalRequest?.url?.startsWith("/v1/auth/");
    if (
      error.response?.status === 401 &&
      !originalRequest?._retry &&
      !isAuthEndpoint
    ) {
      if (isRefreshing) {
        // Another request is already refreshing — queue this one
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(client(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshTokenValue = tokenStore.getRefreshToken();
      if (!refreshTokenValue) {
        isRefreshing = false;
        processQueue(error, null);
        redirectToLogin();
        return Promise.reject(normaliseError(error));
      }

      try {
        // Call refresh directly with axios to avoid our interceptors
        const { data } = await axios.post(
          `${client.defaults.baseURL}/v1/auth/refresh`,
          { refresh_token: refreshTokenValue },
          { headers: { "Content-Type": "application/json" } },
        );

        const tokens = data?.data ?? data;
        tokenStore.setAccessToken(tokens.access_token);
        tokenStore.setRefreshToken(tokens.refresh_token);

        isRefreshing = false;
        processQueue(null, tokens.access_token);

        // Retry the original request with the new token
        originalRequest.headers.Authorization = `Bearer ${tokens.access_token}`;
        return client(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        processQueue(refreshError, null);
        tokenStore.clear();
        redirectToLogin();
        return Promise.reject(normaliseError(error));
      }
    }

    return Promise.reject(normaliseError(error));
  },
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function normaliseError(error: AxiosError): ApiError {
  const data = error.response?.data as
    | { message?: string; field_errors?: Record<string, string[]> }
    | undefined;

  return {
    message:
      data?.message ||
      error.message ||
      "An unexpected error occurred",
    statusCode: error.response?.status ?? 500,
    field_errors: data?.field_errors,
  };
}

function redirectToLogin() {
  // Only redirect if we're not already on an auth page
  if (!window.location.pathname.startsWith("/login")) {
    window.location.href = "/login";
  }
}

/**
 * Extract a human-readable message from any error shape.
 * Useful as a default for toast.error(getErrorMessage(error)).
 */
export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) return error.message;
  if (error instanceof Error) return error.message;
  return "An unexpected error occurred";
}

function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    "statusCode" in error
  );
}
